import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { PageLoader, TooltipProvider } from '@/components/ui';
import { ApiError, refreshSession } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { connectSocket, disconnectSocket } from '@/lib/socket';
import { Layout } from './Layout';

const LoginPage = lazy(() => import('@/features/auth/LoginPage'));
const AuthComplete = lazy(() => import('@/features/auth/AuthComplete'));
const MyWorkPage = lazy(() => import('@/features/my-work/MyWorkPage'));
const InboxPage = lazy(() => import('@/features/inbox/InboxPage'));
const ProjectsPage = lazy(() => import('@/features/projects/ProjectsPage'));
const ProjectLayout = lazy(() => import('@/features/projects/ProjectLayout'));
const BacklogPage = lazy(() => import('@/features/backlog/BacklogPage'));
const BoardPage = lazy(() => import('@/features/boards/BoardPage'));
const TriagePage = lazy(() => import('@/features/triage/TriagePage'));
const ReleasesPage = lazy(() => import('@/features/releases/ReleasesPage'));
const ReleasePage = lazy(() => import('@/features/releases/ReleasePage'));
const BuildPage = lazy(() => import('@/features/releases/BuildPage'));
const ProjectSettingsPage = lazy(() => import('@/features/projects/ProjectSettingsPage'));
const ProjectItemsPage = lazy(() => import('@/features/projects/ProjectItemsPage'));
const ItemPage = lazy(() => import('@/features/items/ItemPage'));
const NotesPage = lazy(() => import('@/features/notes/NotesPage'));
const TimePage = lazy(() => import('@/features/time/TimePage'));
const DocumentsPage = lazy(() => import('@/features/documents/DocumentsPage'));
const DocumentPage = lazy(() => import('@/features/documents/DocumentPage'));
const ExtractionPage = lazy(() => import('@/features/documents/ExtractionPage'));
const TestsPage = lazy(() => import('@/features/testing/TestsPage'));
const RunPage = lazy(() => import('@/features/testing/RunPage'));
const ReportsPage = lazy(() => import('@/features/reports/ReportsPage'));
const RoadmapPage = lazy(() => import('@/features/planning/RoadmapPage'));
const DashboardsPage = lazy(() => import('@/features/reports/DashboardsPage'));
const ApprovalsPage = lazy(() => import('@/features/approvals/ApprovalsPage'));
const RetroPage = lazy(() => import('@/features/planning/RetroPage'));
const ChatPage = lazy(() => import('@/features/chat/ChatPage'));
const StandupsPage = lazy(() => import('@/features/chat/StandupsPage'));
const PagesPage = lazy(() => import('@/features/pages/PagesPage'));
const PageView = lazy(() => import('@/features/pages/PagesPage').then((m) => ({ default: m.PageView })));
const MediaPage = lazy(() => import('@/features/media/MediaPage'));
const SearchPage = lazy(() => import('@/features/search/SearchPage'));
const AdminPage = lazy(() => import('@/features/admin/AdminPage'));
const ProfilePage = lazy(() => import('@/features/profile/ProfilePage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
      refetchOnWindowFocus: true,
    },
  },
});

/** Restores the session from the refresh cookie, then connects realtime. */
function AuthGate({ children }: { children: React.ReactNode }) {
  const { token, ready } = useAuth();
  const location = useLocation();
  useEffect(() => {
    if (!ready) void refreshSession().then((ok) => !ok && useAuth.getState().clear());
  }, [ready]);
  useEffect(() => {
    if (token) connectSocket(queryClient);
    else disconnectSocket();
  }, [token]);
  // Refresh the access token shortly before it expires.
  useEffect(() => {
    if (!token) return;
    const ms = Math.max(30_000, useAuth.getState().expiresAt - Date.now() - 60_000);
    const t = setTimeout(() => void refreshSession(), ms);
    return () => clearTimeout(t);
  }, [token]);
  if (!ready) return <PageLoader />;
  if (!token) return <Navigate to={`/login?returnTo=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  return <>{children}</>;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/auth/complete" element={<AuthComplete />} />
              <Route
                element={
                  <AuthGate>
                    <Layout />
                  </AuthGate>
                }
              >
                <Route index element={<MyWorkPage />} />
                <Route path="inbox" element={<InboxPage />} />
                <Route path="notes" element={<NotesPage />} />
                <Route path="notes/:id" element={<NotesPage />} />
                <Route path="time" element={<TimePage />} />
                <Route path="search" element={<SearchPage />} />
                <Route path="dashboards" element={<DashboardsPage />} />
                <Route path="approvals" element={<ApprovalsPage />} />
                <Route path="chat" element={<ChatPage />} />
                <Route path="chat/:channelId" element={<ChatPage />} />
                <Route path="standups" element={<StandupsPage />} />
                <Route path="pages/:id" element={<PageView />} />
                <Route path="media/:id" element={<MediaPage />} />
                <Route path="sprints/:id/retro" element={<RetroPage />} />
                <Route path="admin/*" element={<AdminPage />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="library" element={<DocumentsPage />} />
                <Route path="projects" element={<ProjectsPage />} />
                <Route path="p/:key" element={<ProjectLayout />}>
                  <Route index element={<Navigate to="board" replace />} />
                  <Route path="backlog" element={<BacklogPage />} />
                  <Route path="roadmap" element={<RoadmapPage />} />
                  <Route path="board" element={<BoardPage />} />
                  <Route path="board/:boardId" element={<BoardPage />} />
                  <Route path="items" element={<ProjectItemsPage />} />
                  <Route path="triage" element={<TriagePage />} />
                  <Route path="releases" element={<ReleasesPage />} />
                  <Route path="documents" element={<DocumentsPage />} />
                  <Route path="tests" element={<TestsPage />} />
                  <Route path="reports" element={<ReportsPage />} />
                  <Route path="pages" element={<PagesPage />} />
                  <Route path="settings" element={<ProjectSettingsPage />} />
                </Route>
                <Route path="items/:key" element={<ItemPage />} />
                <Route path="releases/:id" element={<ReleasePage />} />
                <Route path="builds/:id" element={<BuildPage />} />
                <Route path="test-runs/:id" element={<RunPage />} />
                <Route path="documents/:id" element={<DocumentPage />} />
                <Route path="extractions/:id" element={<ExtractionPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster richColors position="bottom-right" closeButton />
      </TooltipProvider>
    </QueryClientProvider>
  );
}
